/**
 * Public match configuration shared by the game client and memocart-admin.
 *
 * `publicMatch.mode` is the source of truth. `flags` are derived and always
 * agree with `mode`. Writers send `mode`; readers may use either.
 *
 * See docs/game-config.md for the HTTP contract.
 */

export type PublicMatchMode = 'checkpointsAsNitro' | 'nitroSpawnersOnly';

export interface PublicMatchConfig {
  mode: PublicMatchMode;
  /** ISO-8601 */
  updatedAt: string;
  /** Admin id, or null when the document is the default. */
  updatedBy: string | null;
}

export interface GameConfigFlags {
  /** Groups of 5 pads advance the lap in order and grant nitro. */
  checkpointsAsNitro: boolean;
  /** The same pads only grant nitro. Laps count by distance, not by sector order. */
  nitroSpawnersOnly: boolean;
}

export interface GameConfigDocument {
  publicMatch: PublicMatchConfig;
  flags: GameConfigFlags;
}

export const GAME_CONFIG_STORAGE_KEY = 'memocart.skr.game-config.v1';
export const ADMIN_STORAGE_KEY = 'memocart.skr.admin.v1';

/** Local stand-in until memocart-api checks an admin allow-list. Override with VITE_ADMIN_CODE. */
export const DEFAULT_ADMIN_CODE = 'memocart-admin';

export function flagsForMode(mode: PublicMatchMode): GameConfigFlags {
  return {
    checkpointsAsNitro: mode === 'checkpointsAsNitro',
    nitroSpawnersOnly: mode === 'nitroSpawnersOnly',
  };
}

export function isPublicMatchMode(value: unknown): value is PublicMatchMode {
  return value === 'checkpointsAsNitro' || value === 'nitroSpawnersOnly';
}

export function defaultGameConfig(): GameConfigDocument {
  return gameConfigDocument('checkpointsAsNitro', null, new Date(0).toISOString());
}

export function gameConfigDocument(
  mode: PublicMatchMode,
  updatedBy: string | null,
  updatedAt = new Date().toISOString(),
): GameConfigDocument {
  return {
    publicMatch: { mode, updatedAt, updatedBy },
    flags: flagsForMode(mode),
  };
}

/** Rebuild flags from `mode` so a stale or hostile payload cannot disagree with itself. */
export function sanitizeGameConfig(raw: unknown): GameConfigDocument {
  const fallback = defaultGameConfig();
  if (!raw || typeof raw !== 'object') return fallback;
  const data = raw as { publicMatch?: { mode?: unknown; updatedAt?: unknown; updatedBy?: unknown } };
  const mode = isPublicMatchMode(data.publicMatch?.mode) ? data.publicMatch.mode : fallback.publicMatch.mode;
  const updatedAt = typeof data.publicMatch?.updatedAt === 'string' && data.publicMatch.updatedAt
    ? data.publicMatch.updatedAt
    : fallback.publicMatch.updatedAt;
  const updatedBy = typeof data.publicMatch?.updatedBy === 'string' && data.publicMatch.updatedBy
    ? data.publicMatch.updatedBy
    : null;
  return gameConfigDocument(mode, updatedBy, updatedAt);
}
